import { create } from "zustand";
import { GRANDMOTHER_NODE_ID, createInitialPatchDocument } from "../domain/initial-patch";
import { primaryInstrumentNode } from "../instruments/catalog";
import {
  cableEndpoints,
  clonePatchDocument,
  migratePatchDocument,
  type Cable,
  type CableEndpoint,
  type PatchDocument,
  type ExternalEndpointNode,
  type UtilityNode,
} from "../domain/patch";
import {
  deleteStoredPatch,
  listStoredPatches,
  loadStoredPatch,
  persistPatch,
  storeAttachmentBinary,
  storeImportedPatches,
  type AttachmentBinary,
} from "../persistence/database";
import { enqueuePersistence } from "../persistence/write-queue";

export type { Cable, CableEndpoint } from "../domain/patch";

export type ToolMode = "select" | "patch" | "pan" | "help";
export type ViewMode = "panel" | "normalled" | "flow" | "list";
export type InspectorTab = "info" | "notes" | "photos" | "audio";
export type PersistenceStatus = "loading" | "unsaved" | "saving" | "saved" | "recovered" | "error";

export type PatchSummary = {
  id: string;
  title: string;
  author?: string;
  updatedAt: string;
  category?: string;
  tags: string[];
  favourite: boolean;
  description: string;
  tempo?: number;
  cableCount: number;
  instrument: string;
  hasNotes: boolean;
  photoCount: number;
  audioCount: number;
  documentationText: string;
  featuredPhotoId?: string;
  featuredPhotoTitle?: string;
  featuredAudioId?: string;
  featuredAudioTitle?: string;
  featuredAudioDuration?: number;
};

export type PatchMetadataUpdate = {
  title: string;
  author?: string;
  category?: string;
  tags: string[];
  favourite: boolean;
  description: string;
  tempo?: number;
};

export type PrintSettingsUpdate = PatchDocument["printSettings"];
export type CableUpdate = Partial<
  Pick<Cable, "label" | "colour" | "endpointA" | "endpointB" | "prepared" | "branches" | "junction">
>;
export type CableCreate = Pick<Cable, "colour" | "endpointA" | "endpointB"> & {
  label?: string;
  prepared?: boolean;
  branches?: CableEndpoint[];
  junction?: { x: number; y: number };
};
export type ExternalEndpointCreate = Omit<ExternalEndpointNode, "kind" | "id">;
export type ExternalEndpointUpdate = Partial<ExternalEndpointCreate>;
export type UtilityCreate = Omit<UtilityNode, "kind" | "id">;
export type UtilityUpdate = Partial<UtilityCreate>;
export type AttachmentUpdate = Partial<
  Pick<PatchDocument["attachments"][number], "title" | "description" | "featured" | "tags">
>;
export type CustomSettingUpdate = Partial<
  Pick<PatchDocument["customSettings"][number], "label" | "value" | "category">
>;
export type PerformanceStateUpdate = Partial<
  Pick<PatchDocument["performanceStates"][number], "name" | "notes">
>;

export type ImportDocumentsOptions = {
  conflict: "keep-both" | "replace";
  replaceLibrary?: boolean;
  preferredActivePatchId?: string;
  attachments?: AttachmentBinary[];
};

export type ImportDocumentsResult = {
  imported: number;
  copied: number;
  activePatchId: string;
};

type HistoryEntry = {
  label: string;
  document: PatchDocument;
};

type HistoryTransaction = {
  label: string;
  before: PatchDocument;
  changed: boolean;
};

export type EditorStore = {
  tool: ToolMode;
  viewMode: ViewMode;
  focusedSectionId: string | null;
  sectionFocusRevision: number;
  leftPanelOpen: boolean;
  inspectorOpen: boolean;
  inspectorTab: InspectorTab;
  recallOpen: boolean;
  selectedId: string | null;
  pendingPortId: string | null;
  activeDocument: PatchDocument;
  patchLibrary: PatchSummary[];
  openPatchIds: string[];
  libraryOperation: string | null;
  libraryError: string | null;
  controlValues: Record<string, number>;
  cables: Cable[];
  hydrationState: "idle" | "loading" | "ready" | "error";
  persistenceStatus: PersistenceStatus;
  persistenceError: string | null;
  lastSavedAt: string | null;
  documentRevision: number;
  saveUrgency: "debounced" | "immediate";
  past: HistoryEntry[];
  future: HistoryEntry[];
  historyTransaction: HistoryTransaction | null;
  canUndo: boolean;
  canRedo: boolean;
  setTool: (tool: ToolMode) => void;
  setViewMode: (viewMode: ViewMode) => void;
  focusSection: (sectionId: string | null) => void;
  toggleLeftPanel: () => void;
  toggleInspector: () => void;
  setInspectorTab: (tab: InspectorTab) => void;
  toggleRecall: () => void;
  select: (id: string | null) => void;
  setControlValue: (id: string, value: number) => void;
  activatePort: (portId: string) => void;
  createExternalEndpoint: (endpoint: ExternalEndpointCreate) => string;
  updateExternalEndpoint: (endpointId: string, update: ExternalEndpointUpdate) => void;
  deleteExternalEndpoint: (endpointId: string) => void;
  createUtility: (utility: UtilityCreate) => string;
  updateUtility: (utilityId: string, update: UtilityUpdate) => void;
  setUtilityControlValue: (utilityId: string, controlId: string, value: number) => void;
  deleteUtility: (utilityId: string) => void;
  finishLooseCable: (x: number, y: number) => void;
  cancelCable: () => void;
  createCable: (cable: CableCreate) => string;
  updateCable: (cableId: string, update: CableUpdate) => void;
  deleteCable: (cableId: string) => void;
  deleteSelectedCable: () => void;
  beginHistoryTransaction: (label: string) => void;
  endHistoryTransaction: () => void;
  undo: () => void;
  redo: () => void;
  createNewPatch: (definitionId?: string) => void;
  openPatch: (patchId: string) => Promise<void>;
  closePatch: (patchId: string) => Promise<void>;
  duplicatePatch: (patchId: string) => Promise<void>;
  deletePatch: (patchId: string) => Promise<void>;
  updatePatchMetadata: (update: PatchMetadataUpdate) => void;
  updatePatchNotes: (body: string) => void;
  addAnnotation: (
    target: PatchDocument["workspace"]["annotations"][number]["target"],
    text: string,
  ) => void;
  updateAnnotation: (annotationId: string, text: string) => void;
  removeAnnotation: (annotationId: string) => void;
  addAttachment: (kind: "photo" | "audio", file: File, duration?: number) => Promise<void>;
  updateAttachment: (attachmentId: string, update: AttachmentUpdate) => void;
  removeAttachment: (attachmentId: string) => void;
  addCustomSetting: (
    label: string,
    value: string,
    category: PatchDocument["customSettings"][number]["category"],
  ) => void;
  updateCustomSetting: (settingId: string, update: CustomSettingUpdate) => void;
  removeCustomSetting: (settingId: string) => void;
  capturePerformanceState: (name: string, notes?: string) => void;
  updatePerformanceState: (stateId: string, update: PerformanceStateUpdate) => void;
  applyPerformanceState: (stateId: string) => void;
  removePerformanceState: (stateId: string) => void;
  movePerformanceState: (stateId: string, direction: -1 | 1) => void;
  updatePrintSettings: (update: PrintSettingsUpdate) => void;
  importDocuments: (
    documents: PatchDocument[],
    options: ImportDocumentsOptions,
  ) => Promise<ImportDocumentsResult>;
  requestSave: () => void;
  beginHydration: () => void;
  hydrateDocument: (
    document: PatchDocument,
    recoveredDraft: boolean,
    library?: PatchDocument[],
  ) => void;
  failHydration: (message: string) => void;
  markSaving: (revision: number) => void;
  markSaved: (revision: number, savedAt: string) => void;
  markSaveError: (message: string) => void;
};

const cableColours = ["#6ee672", "#ef6b55", "#60a8df", "#f2d45c", "#b784e2"];
const HISTORY_LIMIT = 100;

function instrumentNode(document: PatchDocument) {
  return primaryInstrumentNode(document);
}

function nodeIdForPortId(portId: string) {
  if (portId.startsWith("external:")) return portId.slice("external:".length);
  if (portId.startsWith("utility:")) {
    return portId.slice("utility:".length).split(":")[0] ?? GRANDMOTHER_NODE_ID;
  }
  return GRANDMOTHER_NODE_ID;
}

function materializeDocument(document: PatchDocument) {
  return {
    controlValues: { ...(instrumentNode(document)?.controlValues ?? {}) },
    cables: clonePatchDocument(document).workspace.cableAssemblies,
  };
}

function summarizePatch(document: PatchDocument): PatchSummary {
  const photos = document.attachments.filter((attachment) => attachment.kind === "photo");
  const audio = document.attachments.filter((attachment) => attachment.kind === "audio");
  const featuredPhoto = photos.find((attachment) => attachment.featured) ?? photos[0];
  const featuredAudio = audio.find((attachment) => attachment.featured) ?? audio[0];
  return {
    id: document.id,
    title: document.title,
    author: document.author,
    updatedAt: document.updatedAt,
    category: document.metadata.category,
    tags: [...document.metadata.tags],
    favourite: document.metadata.favourite,
    description: document.metadata.description,
    tempo: document.metadata.tempo,
    cableCount: document.workspace.cableAssemblies.length,
    instrument:
      document.workspace.nodes.find((node) => node.kind === "instrument")?.displayName ??
      "Unknown instrument",
    hasNotes: Boolean(document.notes.body.trim() || document.workspace.annotations.length),
    photoCount: photos.length,
    audioCount: audio.length,
    documentationText: [
      document.notes.body,
      ...document.workspace.annotations.map((annotation) => annotation.text),
      ...document.workspace.nodes.flatMap((node) =>
        node.kind === "external-endpoint"
          ? [
              node.displayName,
              node.deviceName ?? "",
              node.portName ?? "",
              node.description ?? "",
              node.voltageNotes ?? "",
            ]
          : node.kind === "utility"
            ? [
                node.displayName,
                node.manufacturer ?? "",
                node.model ?? "",
                node.userNotes ?? "",
                ...node.ports.map((port) => port.label),
              ]
            : [],
      ),
      ...document.workspace.cableAssemblies.map((cable) => cable.label ?? ""),
      ...document.attachments.flatMap((attachment) => [
        attachment.title,
        attachment.description,
        ...attachment.tags,
      ]),
    ].join(" "),
    featuredPhotoId: featuredPhoto?.id,
    featuredPhotoTitle: featuredPhoto?.title,
    featuredAudioId: featuredAudio?.id,
    featuredAudioTitle: featuredAudio?.title,
    featuredAudioDuration: featuredAudio?.duration,
  };
}

function upsertSummary(library: PatchSummary[], document: PatchDocument) {
  return [summarizePatch(document), ...library.filter((patch) => patch.id !== document.id)].sort(
    (a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
  );
}

function nextUpdatedAt(previous: string) {
  const previousTime = Date.parse(previous);
  return new Date(
    Math.max(Date.now(), Number.isFinite(previousTime) ? previousTime + 1 : 0),
  ).toISOString();
}

function updateDocumentTimestamp(document: PatchDocument, previousTimestamp: string) {
  document.updatedAt = nextUpdatedAt(previousTimestamp);
}

function nextCableNumber(cables: Cable[]) {
  return cables.reduce((maximum, cable) => Math.max(maximum, cable.number), 0) + 1;
}

function newId(prefix: string) {
  return globalThis.crypto?.randomUUID?.() ?? `${prefix}-${Date.now()}-${Math.random()}`;
}

const initialDocument = createInitialPatchDocument();
const initialMaterialized = materializeDocument(initialDocument);
const hasRoomForDockedInspector = typeof window !== "undefined" ? window.innerWidth >= 2200 : true;

export const useEditorStore = create<EditorStore>((set, get) => {
  const commitDocument = (
    label: string,
    recipe: (document: PatchDocument) => void,
    urgency: "debounced" | "immediate" = "debounced",
  ) => {
    const state = get();
    const before = state.activeDocument;
    const next = clonePatchDocument(before);
    recipe(next);
    updateDocumentTimestamp(next, before.updatedAt);

    let past = state.past;
    let future = state.future;
    let historyTransaction = state.historyTransaction;
    if (historyTransaction) {
      historyTransaction = { ...historyTransaction, changed: true };
    } else {
      past = [...past, { label, document: clonePatchDocument(before) }].slice(-HISTORY_LIMIT);
      future = [];
    }

    set({
      activeDocument: next,
      patchLibrary: upsertSummary(state.patchLibrary, next),
      ...materializeDocument(next),
      selectedId: state.selectedId,
      persistenceStatus: "unsaved",
      persistenceError: null,
      documentRevision: state.documentRevision + 1,
      saveUrgency: urgency,
      past,
      future,
      historyTransaction,
      canUndo: historyTransaction ? state.canUndo : past.length > 0,
      canRedo: future.length > 0,
    });
  };

  const restoreFromHistory = (document: PatchDocument) => {
    const state = get();
    const restored = clonePatchDocument(document);
    updateDocumentTimestamp(restored, state.activeDocument.updatedAt);
    return {
      activeDocument: restored,
      ...materializeDocument(restored),
      persistenceStatus: "unsaved" as const,
      persistenceError: null,
      documentRevision: state.documentRevision + 1,
      saveUrgency: "immediate" as const,
      pendingPortId: null,
    };
  };

  const deleteCableById = (cableId: string) => {
    const state = get();
    if (!state.cables.some((cable) => cable.id === cableId)) return;
    commitDocument(
      "Delete patch cable",
      (document) => {
        document.workspace.cableAssemblies = document.workspace.cableAssemblies.filter(
          (cable) => cable.id !== cableId,
        );
        document.workspace.annotations = document.workspace.annotations.filter(
          (annotation) =>
            annotation.target.kind !== "cable" || annotation.target.cableId !== cableId,
        );
      },
      "immediate",
    );
    if (get().selectedId === cableId) set({ selectedId: null });
  };

  return {
    tool: "select",
    viewMode: "panel",
    focusedSectionId: null,
    sectionFocusRevision: 0,
    leftPanelOpen: true,
    inspectorOpen: hasRoomForDockedInspector,
    inspectorTab: "info",
    recallOpen: false,
    selectedId: "filter-cutoff",
    pendingPortId: null,
    activeDocument: initialDocument,
    patchLibrary: [summarizePatch(initialDocument)],
    openPatchIds: [initialDocument.id],
    libraryOperation: null,
    libraryError: null,
    ...initialMaterialized,
    hydrationState: "idle",
    persistenceStatus: "loading",
    persistenceError: null,
    lastSavedAt: null,
    documentRevision: 0,
    saveUrgency: "debounced",
    past: [],
    future: [],
    historyTransaction: null,
    canUndo: false,
    canRedo: false,
    setTool: (tool) => set({ tool, pendingPortId: tool === "patch" ? get().pendingPortId : null }),
    setViewMode: (viewMode) => set({ viewMode }),
    focusSection: (focusedSectionId) =>
      set((state) => ({
        focusedSectionId,
        sectionFocusRevision: state.sectionFocusRevision + 1,
        viewMode: state.viewMode === "list" ? "panel" : state.viewMode,
      })),
    toggleLeftPanel: () => set((state) => ({ leftPanelOpen: !state.leftPanelOpen })),
    toggleInspector: () => set((state) => ({ inspectorOpen: !state.inspectorOpen })),
    setInspectorTab: (inspectorTab) => set({ inspectorOpen: true, inspectorTab }),
    toggleRecall: () => set((state) => ({ recallOpen: !state.recallOpen })),
    select: (selectedId) => set({ selectedId }),
    setControlValue: (id, value) => {
      const clamped = Math.min(1, Math.max(0, value));
      const state = get();
      const current = state.controlValues[id];
      if (current !== undefined && Math.abs(current - clamped) < Number.EPSILON) {
        set({ selectedId: id });
        return;
      }
      commitDocument("Adjust control", (document) => {
        const node = instrumentNode(document);
        if (node) node.controlValues[id] = clamped;
      });
      set({ selectedId: id });
    },
    createExternalEndpoint: (endpoint) => {
      const id = newId("external");
      commitDocument(
        "Add external endpoint",
        (document) => {
          document.workspace.nodes.push({
            ...structuredClone(endpoint),
            kind: "external-endpoint",
            id,
          });
        },
        "immediate",
      );
      set({ selectedId: id });
      return id;
    },
    updateExternalEndpoint: (endpointId, update) => {
      const endpoint = get().activeDocument.workspace.nodes.find(
        (node) => node.kind === "external-endpoint" && node.id === endpointId,
      );
      if (!endpoint) return;
      commitDocument(
        "Edit external endpoint",
        (document) => {
          const target = document.workspace.nodes.find(
            (node) => node.kind === "external-endpoint" && node.id === endpointId,
          );
          if (!target || target.kind !== "external-endpoint") return;
          Object.assign(target, structuredClone(update));
        },
        "immediate",
      );
      set({ selectedId: endpointId });
    },
    deleteExternalEndpoint: (endpointId) => {
      const state = get();
      if (
        !state.activeDocument.workspace.nodes.some(
          (node) => node.kind === "external-endpoint" && node.id === endpointId,
        )
      )
        return;
      const portId = `external:`;
      const referencesEndpoint = (cable: Cable) =>
        cableEndpoints(cable).some(
          (endpoint) => endpoint.kind === "port" && endpoint.nodeId === endpointId,
        );
      commitDocument(
        "Delete external endpoint",
        (document) => {
          document.workspace.nodes = document.workspace.nodes.filter(
            (node) => node.id !== endpointId,
          );
          document.workspace.cableAssemblies = document.workspace.cableAssemblies.filter(
            (cable) => !referencesEndpoint(cable),
          );
          document.workspace.annotations = document.workspace.annotations.filter(
            (annotation) =>
              annotation.target.kind !== "port" || annotation.target.portId !== portId,
          );
          for (const performanceState of document.performanceStates) {
            performanceState.cableAssemblies = performanceState.cableAssemblies.filter(
              (cable) => !referencesEndpoint(cable),
            );
          }
        },
        "immediate",
      );
      if (get().selectedId === endpointId) set({ selectedId: null });
    },
    createUtility: (utility) => {
      const id = newId("utility");
      commitDocument(
        "Add utility",
        (document) => {
          document.workspace.nodes.push({
            ...structuredClone(utility),
            kind: "utility",
            id,
          });
        },
        "immediate",
      );
      set({ selectedId: id });
      return id;
    },
    updateUtility: (utilityId, update) => {
      if (
        !get().activeDocument.workspace.nodes.some(
          (node) => node.kind === "utility" && node.id === utilityId,
        )
      )
        return;
      commitDocument(
        "Edit utility",
        (document) => {
          const target = document.workspace.nodes.find(
            (node) => node.kind === "utility" && node.id === utilityId,
          );
          if (!target || target.kind !== "utility") return;
          Object.assign(target, structuredClone(update));
        },
        "immediate",
      );
      set({ selectedId: utilityId });
    },
    setUtilityControlValue: (utilityId, controlId, value) => {
      const clamped = Math.min(1, Math.max(0, value));
      const utility = get().activeDocument.workspace.nodes.find(
        (node) => node.kind === "utility" && node.id === utilityId,
      );
      const control =
        utility?.kind === "utility"
          ? utility.controls.find((candidate) => candidate.id === controlId)
          : null;
      if (!control || Math.abs(control.value - clamped) < Number.EPSILON) return;
      commitDocument("Adjust utility control", (document) => {
        const target = document.workspace.nodes.find(
          (node) => node.kind === "utility" && node.id === utilityId,
        );
        if (!target || target.kind !== "utility") return;
        const targetControl = target.controls.find((candidate) => candidate.id === controlId);
        if (targetControl) targetControl.value = clamped;
      });
      set({ selectedId: "utility:" + utilityId + ":control:" + controlId });
    },
    deleteUtility: (utilityId) => {
      const state = get();
      if (
        !state.activeDocument.workspace.nodes.some(
          (node) => node.kind === "utility" && node.id === utilityId,
        )
      )
        return;
      const referencesUtility = (cable: Cable) =>
        cableEndpoints(cable).some(
          (endpoint) => endpoint.kind === "port" && endpoint.nodeId === utilityId,
        );
      commitDocument(
        "Delete utility",
        (document) => {
          document.workspace.nodes = document.workspace.nodes.filter(
            (node) => node.id !== utilityId,
          );
          document.workspace.cableAssemblies = document.workspace.cableAssemblies.filter(
            (cable) => !referencesUtility(cable),
          );
          document.workspace.annotations = document.workspace.annotations.filter(
            (annotation) =>
              annotation.target.kind !== "port" ||
              !annotation.target.portId.startsWith("utility:" + utilityId + ":"),
          );
          for (const performanceState of document.performanceStates) {
            performanceState.cableAssemblies = performanceState.cableAssemblies.filter(
              (cable) => !referencesUtility(cable),
            );
          }
        },
        "immediate",
      );
      if (get().selectedId === utilityId) set({ selectedId: null });
    },
    activatePort: (portId) => {
      const state = get();
      if (state.tool !== "patch") {
        set({ selectedId: portId });
        return;
      }
      if (!state.pendingPortId) {
        set({ pendingPortId: portId, selectedId: portId });
        return;
      }
      if (state.pendingPortId === portId) {
        set({ pendingPortId: null });
        return;
      }

      const sourcePortId = state.pendingPortId;
      const number = nextCableNumber(state.cables);
      commitDocument(
        "Add patch cable",
        (document) => {
          document.workspace.cableAssemblies.push({
            id: newId("cable"),
            number,
            colour: cableColours[(number - 1) % cableColours.length],
            endpointA: {
              kind: "port",
              nodeId: nodeIdForPortId(sourcePortId),
              portId: sourcePortId,
            },
            endpointB: { kind: "port", nodeId: nodeIdForPortId(portId), portId },
          });
        },
        "immediate",
      );
      set({ pendingPortId: null, selectedId: portId });
    },
    finishLooseCable: (x, y) => {
      const state = get();
      if (!state.pendingPortId) return;
      const sourcePortId = state.pendingPortId;
      const number = nextCableNumber(state.cables);
      commitDocument(
        "Add loose patch cable",
        (document) => {
          document.workspace.cableAssemblies.push({
            id: newId("cable"),
            number,
            colour: cableColours[(number - 1) % cableColours.length],
            endpointA: {
              kind: "port",
              nodeId: nodeIdForPortId(sourcePortId),
              portId: sourcePortId,
            },
            endpointB: { kind: "loose", x, y },
          });
        },
        "immediate",
      );
      set({ pendingPortId: null });
    },
    cancelCable: () => set({ pendingPortId: null }),
    createCable: (cable) => {
      const state = get();
      const id = newId("cable");
      const number = nextCableNumber(state.cables);
      commitDocument(
        "Add patch cable",
        (document) => {
          document.workspace.cableAssemblies.push({
            id,
            number,
            label: cable.label?.trim() || undefined,
            colour: cable.colour,
            endpointA: structuredClone(cable.endpointA),
            endpointB: structuredClone(cable.endpointB),
            prepared: cable.prepared || undefined,
            branches: cable.branches?.length ? structuredClone(cable.branches) : undefined,
            junction: cable.junction ? structuredClone(cable.junction) : undefined,
          });
        },
        "immediate",
      );
      set({ selectedId: id, pendingPortId: null });
      return id;
    },
    updateCable: (cableId, update) => {
      if (!get().cables.some((cable) => cable.id === cableId)) return;
      commitDocument(
        "Edit patch cable",
        (document) => {
          const cable = document.workspace.cableAssemblies.find((item) => item.id === cableId);
          if (!cable) return;
          if (update.label !== undefined) cable.label = update.label.trim() || undefined;
          if (update.colour !== undefined) cable.colour = update.colour;
          if (update.endpointA !== undefined) cable.endpointA = structuredClone(update.endpointA);
          if (update.endpointB !== undefined) cable.endpointB = structuredClone(update.endpointB);
          if (update.prepared !== undefined) cable.prepared = update.prepared || undefined;
          if (update.branches !== undefined) {
            cable.branches = update.branches.length ? structuredClone(update.branches) : undefined;
          }
          if (update.junction !== undefined) cable.junction = structuredClone(update.junction);
        },
        "immediate",
      );
      set({ selectedId: cableId, pendingPortId: null });
    },
    deleteCable: deleteCableById,
    deleteSelectedCable: () => {
      const state = get();
      if (state.selectedId) deleteCableById(state.selectedId);
    },
    beginHistoryTransaction: (label) => {
      const state = get();
      if (state.historyTransaction) return;
      set({
        historyTransaction: {
          label,
          before: clonePatchDocument(state.activeDocument),
          changed: false,
        },
      });
    },
    endHistoryTransaction: () => {
      const state = get();
      const transaction = state.historyTransaction;
      if (!transaction) return;
      const past = transaction.changed
        ? [...state.past, { label: transaction.label, document: transaction.before }].slice(
            -HISTORY_LIMIT,
          )
        : state.past;
      set({
        historyTransaction: null,
        past,
        future: transaction.changed ? [] : state.future,
        canUndo: past.length > 0,
        canRedo: transaction.changed ? false : state.future.length > 0,
      });
    },
    undo: () => {
      get().endHistoryTransaction();
      const state = get();
      const entry = state.past.at(-1);
      if (!entry) return;
      const future = [
        { label: entry.label, document: clonePatchDocument(state.activeDocument) },
        ...state.future,
      ].slice(0, HISTORY_LIMIT);
      const past = state.past.slice(0, -1);
      set({
        ...restoreFromHistory(entry.document),
        past,
        future,
        canUndo: past.length > 0,
        canRedo: true,
      });
    },
    redo: () => {
      get().endHistoryTransaction();
      const state = get();
      const entry = state.future[0];
      if (!entry) return;
      const past = [
        ...state.past,
        { label: entry.label, document: clonePatchDocument(state.activeDocument) },
      ].slice(-HISTORY_LIMIT);
      const future = state.future.slice(1);
      set({
        ...restoreFromHistory(entry.document),
        past,
        future,
        canUndo: true,
        canRedo: future.length > 0,
      });
    },
    createNewPatch: (definitionId) => {
      const document = createInitialPatchDocument({ definitionId, title: "Untitled Patch" });
      set((state) => ({
        activeDocument: document,
        patchLibrary: upsertSummary(state.patchLibrary, document),
        openPatchIds: [...state.openPatchIds, document.id],
        ...materializeDocument(document),
        selectedId: null,
        pendingPortId: null,
        focusedSectionId: null,
        sectionFocusRevision: state.sectionFocusRevision + 1,
        past: [],
        future: [],
        historyTransaction: null,
        canUndo: false,
        canRedo: false,
        persistenceStatus: "unsaved",
        persistenceError: null,
        documentRevision: state.documentRevision + 1,
        saveUrgency: "immediate",
      }));
    },
    openPatch: async (patchId) => {
      const state = get();
      if (patchId === state.activeDocument.id || state.libraryOperation) return;
      set({ libraryOperation: `open:${patchId}`, libraryError: null });
      try {
        await enqueuePersistence(() => persistPatch(state.activeDocument));
        const { document, recoveredDraft } = await enqueuePersistence(() =>
          loadStoredPatch(patchId),
        );
        set((current) => ({
          activeDocument: document,
          patchLibrary: upsertSummary(current.patchLibrary, document),
          openPatchIds: current.openPatchIds.includes(patchId)
            ? current.openPatchIds
            : [...current.openPatchIds, patchId],
          ...materializeDocument(document),
          selectedId: "filter-cutoff",
          pendingPortId: null,
          focusedSectionId: null,
          sectionFocusRevision: current.sectionFocusRevision + 1,
          past: [],
          future: [],
          historyTransaction: null,
          canUndo: false,
          canRedo: false,
          persistenceStatus: recoveredDraft ? "recovered" : "saved",
          persistenceError: null,
          lastSavedAt: recoveredDraft ? null : document.updatedAt,
          documentRevision: current.documentRevision + 1,
          saveUrgency: recoveredDraft ? "immediate" : "debounced",
          libraryOperation: null,
        }));
      } catch (error) {
        set({
          libraryOperation: null,
          libraryError: error instanceof Error ? error.message : String(error),
        });
      }
    },
    closePatch: async (patchId) => {
      const state = get();
      if (!state.openPatchIds.includes(patchId)) return;
      if (patchId !== state.activeDocument.id) {
        set({ openPatchIds: state.openPatchIds.filter((id) => id !== patchId) });
        return;
      }
      const replacement = state.openPatchIds.find((id) => id !== patchId);
      if (!replacement) return;
      set({ openPatchIds: state.openPatchIds.filter((id) => id !== patchId) });
      await get().openPatch(replacement);
    },
    duplicatePatch: async (patchId) => {
      const state = get();
      if (state.libraryOperation) return;
      set({ libraryOperation: `duplicate:${patchId}`, libraryError: null });
      try {
        await enqueuePersistence(() => persistPatch(state.activeDocument));
        const source =
          patchId === state.activeDocument.id
            ? state.activeDocument
            : (await enqueuePersistence(() => loadStoredPatch(patchId, false))).document;
        const now = new Date().toISOString();
        const document = clonePatchDocument(source);
        document.id = newId("patch");
        document.title = `${source.title} Copy`;
        document.createdAt = now;
        document.updatedAt = now;
        document.revision = { number: 1, label: "Working copy" };
        set((current) => ({
          activeDocument: document,
          patchLibrary: upsertSummary(current.patchLibrary, document),
          openPatchIds: [...current.openPatchIds, document.id],
          ...materializeDocument(document),
          selectedId: null,
          pendingPortId: null,
          focusedSectionId: null,
          sectionFocusRevision: current.sectionFocusRevision + 1,
          past: [],
          future: [],
          historyTransaction: null,
          canUndo: false,
          canRedo: false,
          persistenceStatus: "unsaved",
          persistenceError: null,
          documentRevision: current.documentRevision + 1,
          saveUrgency: "immediate",
          libraryOperation: null,
        }));
      } catch (error) {
        set({
          libraryOperation: null,
          libraryError: error instanceof Error ? error.message : String(error),
        });
      }
    },
    deletePatch: async (patchId) => {
      const state = get();
      if (state.libraryOperation) return;
      set({ libraryOperation: `delete:${patchId}`, libraryError: null });
      try {
        await enqueuePersistence(() => deleteStoredPatch(patchId));
        const patchLibrary = state.patchLibrary.filter((patch) => patch.id !== patchId);
        const openPatchIds = state.openPatchIds.filter((id) => id !== patchId);
        if (patchId !== state.activeDocument.id) {
          set({ patchLibrary, openPatchIds, libraryOperation: null });
          return;
        }

        const replacementId = openPatchIds[0] ?? patchLibrary[0]?.id;
        if (replacementId) {
          const { document, recoveredDraft } = await enqueuePersistence(() =>
            loadStoredPatch(replacementId),
          );
          set((current) => ({
            activeDocument: document,
            patchLibrary: upsertSummary(patchLibrary, document),
            openPatchIds: openPatchIds.includes(replacementId)
              ? openPatchIds
              : [...openPatchIds, replacementId],
            ...materializeDocument(document),
            selectedId: null,
            pendingPortId: null,
            focusedSectionId: null,
            sectionFocusRevision: current.sectionFocusRevision + 1,
            past: [],
            future: [],
            historyTransaction: null,
            canUndo: false,
            canRedo: false,
            persistenceStatus: recoveredDraft ? "recovered" : "saved",
            documentRevision: current.documentRevision + 1,
            saveUrgency: recoveredDraft ? "immediate" : "debounced",
            libraryOperation: null,
          }));
          return;
        }

        const document = createInitialPatchDocument({ title: "Untitled Patch" });
        set((current) => ({
          activeDocument: document,
          patchLibrary: [summarizePatch(document)],
          openPatchIds: [document.id],
          ...materializeDocument(document),
          selectedId: null,
          pendingPortId: null,
          focusedSectionId: null,
          sectionFocusRevision: current.sectionFocusRevision + 1,
          past: [],
          future: [],
          historyTransaction: null,
          canUndo: false,
          canRedo: false,
          persistenceStatus: "unsaved",
          documentRevision: current.documentRevision + 1,
          saveUrgency: "immediate",
          libraryOperation: null,
        }));
      } catch (error) {
        set({
          libraryOperation: null,
          libraryError: error instanceof Error ? error.message : String(error),
        });
      }
    },
    updatePatchMetadata: (update) => {
      commitDocument("Edit patch details", (document) => {
        document.title = update.title.trim() || "Untitled Patch";
        document.author = update.author?.trim() || undefined;
        document.metadata.category = update.category?.trim() || undefined;
        document.metadata.tags = [...new Set(update.tags.map((tag) => tag.trim()).filter(Boolean))];
        document.metadata.favourite = update.favourite;
        document.metadata.description = update.description.trim();
        document.metadata.tempo = update.tempo;
      });
    },
    updatePatchNotes: (body) => {
      commitDocument("Edit patch notes", (document) => {
        document.notes.body = body;
      });
    },
    addAnnotation: (target, text) => {
      const cleanText = text.trim();
      if (!cleanText) return;
      commitDocument(
        "Add linked note",
        (document) => {
          document.workspace.annotations.push({
            id: newId("annotation"),
            target: structuredClone(target),
            text: cleanText,
          });
        },
        "immediate",
      );
    },
    updateAnnotation: (annotationId, text) => {
      const cleanText = text.trim();
      if (!cleanText) return;
      commitDocument("Edit linked note", (document) => {
        const annotation = document.workspace.annotations.find((item) => item.id === annotationId);
        if (annotation) annotation.text = cleanText;
      });
    },
    removeAnnotation: (annotationId) => {
      commitDocument(
        "Remove linked note",
        (document) => {
          document.workspace.annotations = document.workspace.annotations.filter(
            (annotation) => annotation.id !== annotationId,
          );
        },
        "immediate",
      );
    },
    addAttachment: async (kind, file, duration) => {
      const attachmentId = newId(kind);
      await enqueuePersistence(() => storeAttachmentBinary({ id: attachmentId, blob: file }));
      commitDocument(
        `Add ${kind}`,
        (document) => {
          document.attachments.push({
            id: attachmentId,
            kind,
            filename: file.name || `${kind}-${document.attachments.length + 1}`,
            mimeType: file.type || (kind === "photo" ? "image/jpeg" : "audio/ogg"),
            size: file.size,
            createdAt: new Date().toISOString(),
            title:
              file.name.replace(/\.[^.]+$/, "") || (kind === "photo" ? "Photo" : "Audio sample"),
            description: "",
            featured: !document.attachments.some((attachment) => attachment.kind === kind),
            tags: [],
            duration,
            rotation: kind === "photo" ? 0 : undefined,
          });
        },
        "immediate",
      );
    },
    updateAttachment: (attachmentId, update) => {
      commitDocument("Edit attachment details", (document) => {
        const attachment = document.attachments.find((item) => item.id === attachmentId);
        if (!attachment) return;
        if (update.featured) {
          for (const item of document.attachments) {
            if (item.kind === attachment.kind) item.featured = item.id === attachmentId;
          }
        }
        if (update.title !== undefined) attachment.title = update.title.trim();
        if (update.description !== undefined) attachment.description = update.description.trim();
        if (update.featured !== undefined) attachment.featured = update.featured;
        if (update.tags !== undefined) {
          attachment.tags = [...new Set(update.tags.map((tag) => tag.trim()).filter(Boolean))];
        }
      });
    },
    removeAttachment: (attachmentId) => {
      commitDocument(
        "Remove attachment",
        (document) => {
          const removed = document.attachments.find((item) => item.id === attachmentId);
          document.attachments = document.attachments.filter((item) => item.id !== attachmentId);
          if (removed?.featured) {
            const replacement = document.attachments.find((item) => item.kind === removed.kind);
            if (replacement) replacement.featured = true;
          }
        },
        "immediate",
      );
    },
    addCustomSetting: (label, value, category) => {
      const cleanLabel = label.trim();
      if (!cleanLabel) return;
      commitDocument("Add custom setting", (document) => {
        document.customSettings.push({
          id: newId("setting"),
          label: cleanLabel,
          value: value.trim(),
          category,
        });
      });
    },
    updateCustomSetting: (settingId, update) => {
      commitDocument("Edit custom setting", (document) => {
        const setting = document.customSettings.find((item) => item.id === settingId);
        if (!setting) return;
        if (update.label !== undefined) setting.label = update.label.trim() || setting.label;
        if (update.value !== undefined) setting.value = update.value.trim();
        if (update.category !== undefined) setting.category = update.category;
      });
    },
    removeCustomSetting: (settingId) => {
      commitDocument("Remove custom setting", (document) => {
        document.customSettings = document.customSettings.filter((item) => item.id !== settingId);
      });
    },
    capturePerformanceState: (name, notes = "") => {
      const cleanName = name.trim();
      if (!cleanName) return;
      commitDocument(
        "Capture performance state",
        (document) => {
          const node = instrumentNode(document);
          document.performanceStates.push({
            id: newId("state"),
            name: cleanName,
            notes: notes.trim(),
            capturedAt: new Date().toISOString(),
            controlValues: { ...(node?.controlValues ?? {}) },
            cableAssemblies: structuredClone(document.workspace.cableAssemblies),
            customSettings: structuredClone(document.customSettings),
          });
        },
        "immediate",
      );
    },
    updatePerformanceState: (stateId, update) => {
      commitDocument("Edit performance state", (document) => {
        const performanceState = document.performanceStates.find((item) => item.id === stateId);
        if (!performanceState) return;
        if (update.name !== undefined) {
          performanceState.name = update.name.trim() || performanceState.name;
        }
        if (update.notes !== undefined) performanceState.notes = update.notes.trim();
      });
    },
    applyPerformanceState: (stateId) => {
      const state = get().activeDocument.performanceStates.find((item) => item.id === stateId);
      if (!state) return;
      commitDocument(
        `Apply performance state ${state.name}`,
        (document) => {
          const node = instrumentNode(document);
          if (node) node.controlValues = structuredClone(state.controlValues);
          document.workspace.cableAssemblies = structuredClone(state.cableAssemblies);
          document.customSettings = structuredClone(state.customSettings);
        },
        "immediate",
      );
      set({ selectedId: null, pendingPortId: null });
    },
    removePerformanceState: (stateId) => {
      commitDocument("Remove performance state", (document) => {
        document.performanceStates = document.performanceStates.filter(
          (item) => item.id !== stateId,
        );
      });
    },
    movePerformanceState: (stateId, direction) => {
      commitDocument("Reorder performance states", (document) => {
        const from = document.performanceStates.findIndex((item) => item.id === stateId);
        if (from < 0) return;
        const to = Math.max(0, Math.min(document.performanceStates.length - 1, from + direction));
        if (from === to) return;
        const [state] = document.performanceStates.splice(from, 1);
        document.performanceStates.splice(to, 0, state!);
      });
    },
    updatePrintSettings: (update) => {
      commitDocument("Change print settings", (document) => {
        document.printSettings = { ...update };
      });
    },
    importDocuments: async (documents, options) => {
      const state = get();
      if (state.libraryOperation) throw new Error("Another library operation is already running.");
      if (documents.length === 0) throw new Error("The bundle contains no patches.");
      set({ libraryOperation: "import", libraryError: null });
      try {
        const validated = documents.map(migratePatchDocument);
        const occupiedIds = new Set(
          options.replaceLibrary ? [] : state.patchLibrary.map((patch) => patch.id),
        );
        const idMap = new Map<string, string>();
        let copied = 0;
        const prepared = validated.map((source) => {
          const document = clonePatchDocument(source);
          if (options.conflict === "keep-both" && occupiedIds.has(document.id)) {
            const originalId = document.id;
            document.id = newId("patch");
            document.title = `${document.title} (Imported)`;
            const now = new Date().toISOString();
            document.createdAt = now;
            document.updatedAt = now;
            document.revision = { number: 1, label: "Imported copy" };
            idMap.set(originalId, document.id);
            copied += 1;
          }
          occupiedIds.add(document.id);
          return document;
        });
        const requestedActive = options.preferredActivePatchId;
        const activePatchId =
          (requestedActive &&
            (idMap.get(requestedActive) ??
              prepared.find((document) => document.id === requestedActive)?.id)) ||
          prepared[0]!.id;

        if (!options.replaceLibrary) {
          await enqueuePersistence(() => persistPatch(state.activeDocument));
        }
        await enqueuePersistence(() =>
          storeImportedPatches(
            prepared,
            activePatchId,
            options.replaceLibrary,
            options.attachments,
          ),
        );
        const library = await enqueuePersistence(() => listStoredPatches());
        const activeDocument =
          library.find((document) => document.id === activePatchId) ?? prepared[0]!;

        set((current) => ({
          activeDocument,
          patchLibrary: library.map(summarizePatch),
          openPatchIds: options.replaceLibrary
            ? [activeDocument.id]
            : [...new Set([...current.openPatchIds, activeDocument.id])],
          ...materializeDocument(activeDocument),
          selectedId: null,
          pendingPortId: null,
          focusedSectionId: null,
          sectionFocusRevision: current.sectionFocusRevision + 1,
          past: [],
          future: [],
          historyTransaction: null,
          canUndo: false,
          canRedo: false,
          persistenceStatus: "saved",
          persistenceError: null,
          lastSavedAt: activeDocument.updatedAt,
          documentRevision: current.documentRevision + 1,
          saveUrgency: "debounced",
          libraryOperation: null,
        }));
        return { imported: prepared.length, copied, activePatchId };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        set({ libraryOperation: null, libraryError: message });
        throw new Error(message, { cause: error });
      }
    },
    requestSave: () =>
      set((state) => ({
        persistenceStatus: "unsaved",
        persistenceError: null,
        documentRevision: state.documentRevision + 1,
        saveUrgency: "immediate",
      })),
    beginHydration: () => set({ hydrationState: "loading", persistenceStatus: "loading" }),
    hydrateDocument: (document, recoveredDraft, library = [document]) =>
      set({
        activeDocument: document,
        patchLibrary: upsertSummary(library.map(summarizePatch), document),
        openPatchIds: [document.id],
        ...materializeDocument(document),
        focusedSectionId: null,
        sectionFocusRevision: 0,
        hydrationState: "ready",
        persistenceStatus: recoveredDraft ? "recovered" : "saved",
        persistenceError: null,
        lastSavedAt: recoveredDraft ? null : document.updatedAt,
        documentRevision: 0,
        saveUrgency: recoveredDraft ? "immediate" : "debounced",
        past: [],
        future: [],
        historyTransaction: null,
        canUndo: false,
        canRedo: false,
      }),
    failHydration: (message) =>
      set({ hydrationState: "error", persistenceStatus: "error", persistenceError: message }),
    markSaving: (revision) => {
      if (get().documentRevision === revision) set({ persistenceStatus: "saving" });
    },
    markSaved: (revision, savedAt) => {
      if (get().documentRevision !== revision) return;
      set({ persistenceStatus: "saved", persistenceError: null, lastSavedAt: savedAt });
    },
    markSaveError: (message) => set({ persistenceStatus: "error", persistenceError: message }),
  };
});
